<template>
  <!-- Display the formatted time with animated pulse between hours and minutes -->

  <span class="font-weight-bold">{{ formattedTime.hours }}</span>
  <span class="pulse font-weight-bold">:</span>
  <span class="font-weight-bold">{{ formattedTime.minutes }}</span>
</template>

<script>
export default {
  name: "ElapsedTimeDisplay",

  /**
   * Props accepted by the component.
   * @prop {String} startTime - The start time in string format (e.g., "12:30:00 01.01.2023"),
   * used to calculate the elapsed time from the start point.
   */
  props: {
    startTime: {
      type: Number,
      required: true,
    },
  },

  /**
   * Component data.
   * @returns {Object} formattedTime - Holds hours and minutes in formatted strings for display.
   * @returns {Number|null} timerInterval - Stores the ID of the active timer interval created by setInterval.
   */
  data() {
    /**
     * Holds the formatted elapsed time to display.
     * @type {Object}
     * @property {String} hours - The formatted hours string (e.g., "2h").
     * @property {String} minutes - The formatted minutes string (e.g., "45min").
     */
    return {
      formattedTime: {
        hours: "0h",
        minutes: "0min",
      },
      /**
       * The ID of the active timer interval.
       * @type {Number|null}
       */
      timerInterval: null,
    };
  },

  methods: {
    /**
     * Starts a timer to update the elapsed time every second.
     * Calculates hours and minutes based on the difference between the current time and start time.
     * Updates `formattedTime` with the calculated values.
     */
    startTimer() {
      const startTime = this.startTime;
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        const now = Date.now(); // Get the current time as a Date object
        const elapsedTime = now - startTime; // Subtract two Date objects (gives milliseconds)

        if (isNaN(elapsedTime)) {
          console.error(
            "Invalid elapsed time. Start time might be invalid:",
            startTime
          );
          this.formattedTime = { hours: "0h", minutes: "0min" };
          return;
        }

        const hours = Math.floor(elapsedTime / (1000 * 60 * 60));
        const minutes = Math.floor(
          (elapsedTime % (1000 * 60 * 60)) / (1000 * 60)
        );

        this.formattedTime = {
          hours: hours + "h",
          minutes: minutes + "min",
        };
      }, 1000);
    },
  },

  /**
   * Lifecycle hook that starts the timer when the component mounts.
   */
  mounted() {
    this.startTimer();
  },

  /**
   * Lifecycle hook that clears the timer when the component is about to be unmounted,
   * to avoid memory leaks.
   */
  beforeUnmount() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  },
};
</script>
